import React from "react";
import { useContent } from "../hooks/useContent";
import { useLang } from "../context/LanguageContext";
import { SmartImage } from "../components/SmartImage";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/motion/Reveal";
import { SectionHeading } from "../components/motion/SectionHeading";
import { Check } from "lucide-react";
import { CTASection } from "../components/home/FinancialBenefits";

export default function About() {
  const { t, tc } = useLang();
  const { data: team } = useContent("team");

  return (
    <>
      <SEO pageKey="about" title={t.seo.about.title} description={t.seo.about.description} />
      <PageHeader pageKey="about"
        overline={t.about.overline}
        title={t.about.title}
        sub={t.about.intro}
        image="https://images.unsplash.com/photo-1694974249671-2a921a644a1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      {/* Vision / Mission */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-8">
          {[
            { label: t.about.vision, text: t.about.visionText },
            { label: t.about.mission, text: t.about.missionText },
          ].map((b, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="bg-green rounded-3xl p-10 text-white h-full grain">
                <span className="overline">{b.label}</span>
                <p className="font-display text-2xl sm:text-3xl mt-4 leading-snug">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values + Philosophy */}
      <section className="py-10 sm:py-14 bg-lightgray">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <SectionHeading overline={t.about.values} title={t.about.philosophy} />
            <Reveal delay={0.1}><p className="text-charcoal/60 leading-relaxed mt-6 text-lg">{t.about.philosophyText}</p></Reveal>
          </div>
          <Stagger className="grid grid-cols-2 gap-0 self-center">
            {t.about.valuesList.map((v, i) => (
              <StaggerItem key={i}>
                <div className="bg-white p-4 sm:p-6 border border-border h-full flex items-center gap-2.5 sm:gap-3">
                  <span className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-green/8 flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-green-light" /></span>
                  <span className="font-display text-sm sm:text-lg text-charcoal leading-tight">{v}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Management */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading overline={t.about.team} title={t.about.teamSub} center />
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-0 mt-10 sm:mt-16">
            {team.map((m) => (
              <StaggerItem key={m.id}>
                <div className="group text-center px-2 sm:px-3" data-testid={`team-${m.id}`}>
                  <div className="overflow-hidden hover-zoom aspect-square mb-3 sm:mb-5">
                    <SmartImage src={m.image} alt={m.name} fit="cover" className="w-full h-full bg-lightgray" />
                  </div>
                  <h3 className="font-display text-base sm:text-xl text-charcoal leading-tight">{m.name}</h3>
                  <p className="text-gold text-[11px] sm:text-sm font-medium mt-1">{tc(m.role)}</p>
                  <p className="text-charcoal/55 text-[11px] sm:text-sm mt-1.5 leading-snug sm:leading-relaxed">{tc(m.bio)}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
      <CTASection />
    </>
  );
}
